import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as RunIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Terminal as LogsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  GridLegacy as Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment-timezone";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { AdminDialog, PageShell } from "@/components/common";
import { useLoading } from "@/hooks/useLoading";
import { CustomerService } from "@/services/features/CustomerService";
import {
  getEtlEnvironmentConfig,
  JobDetails,
  JobService,
  JobStatus,
} from "@/services/features/JobService";
import {
  formatRelativeStoreTime,
  formatStoreDateTimeWithTimezone,
  getBrowserTimezone,
} from "@/utils/storeTime";

import JsonReportViewer, { hasJsonReport } from "./JsonReportViewer";

const etlStatusKeyMap: Record<string, string> = {
  SUCCESSFUL: "SUCCESSFUL",
  FAILED: "FAILED",
  RUNNING: "RUNNING",
  IN_PROGRESS: "inProgress",
  "IN PROGRESS": "inProgress",
  INPROGRESS: "inProgress",
  JOB_STARTED: "jobStarted",
  MACHINE_CREATED: "machineCreated",
  JOB_COMPLETED: "jobCompleted",
  JOB_FAILED: "jobFailed",
  MACHINE_DELETED: "machineDeleted",
  MACHINE_TIMEOUT: "machineTimeout",
};

const normalizeStatus = (status: string) => status.replace(/\s+/g, "_").toUpperCase();

const sortStatusHistory = (statuses: JobStatus[]) =>
  statuses.toSorted((first, second) => {
    const firstDate = moment(first.createdAt);
    const secondDate = moment(second.createdAt);

    return firstDate.valueOf() - secondDate.valueOf();
  });

const buildCloudLogsUrl = (
  instanceId: string | undefined,
  statusHistory: JobStatus[],
  projectId: string,
) => {
  if (!instanceId) {
    return null;
  }

  const query = `resource.type="gce_instance" resource.labels.instance_id="${instanceId}"`;
  const baseUrl = `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(query)}`;

  if (statusHistory.length === 0) {
    return `${baseUrl}?project=${projectId}`;
  }

  const endTime = moment(statusHistory[0].createdAt).utc();
  const startTime = moment(endTime).subtract(1, "hours");
  const timeRange = `${startTime.format("YYYY-MM-DD[T]HH:mm:ss.SSS")}Z/${endTime.format(
    "YYYY-MM-DD[T]HH:mm:ss.SSS",
  )}Z`;

  return `${baseUrl};timeRange=${encodeURIComponent(timeRange)}?project=${projectId}`;
};

const buildStorageConsoleUrl = (path: string | undefined, fallbackBucket: string) => {
  const trimmedPath = path?.trim();

  if (!trimmedPath) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmedPath)) {
    return trimmedPath;
  }

  const gsPathMatch = trimmedPath.match(/^gs:\/\/([^/]+)\/?(.*)$/i);
  const bucket = gsPathMatch?.[1] ?? fallbackBucket;
  const objectPath = (gsPathMatch?.[2] ?? trimmedPath).replace(/^\/+/, "");
  const encodedObjectPath = objectPath.split("/").map(encodeURIComponent).join("/");

  return encodedObjectPath
    ? `https://console.cloud.google.com/storage/browser/${bucket}/${encodedObjectPath}`
    : `https://console.cloud.google.com/storage/browser/${bucket}`;
};

const getElapsedSincePreviousStatus = (statusHistory: JobStatus[], index: number) => {
  if (index === 0) {
    return null;
  }

  const previousStatusTime = moment(statusHistory[index - 1].createdAt);
  const currentStatusTime = moment(statusHistory[index].createdAt);

  if (!previousStatusTime.isValid() || !currentStatusTime.isValid()) {
    return null;
  }

  return moment.duration(currentStatusTime.diff(previousStatusTime));
};

const getTotalJobDuration = (statusHistory: JobStatus[]) => {
  if (statusHistory.length < 2) {
    return null;
  }

  const firstStatusTime = moment(statusHistory[0].createdAt);
  const lastStatusTime = moment(statusHistory[statusHistory.length - 1].createdAt);

  if (!firstStatusTime.isValid() || !lastStatusTime.isValid()) {
    return null;
  }

  return moment.duration(lastStatusTime.diff(firstStatusTime));
};

const ETLRunDetails: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = useLoading();

  const [job, setJob] = useState<JobDetails | null>(null);
  const [customerName, setCustomerName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [showRunDialog, setShowRunDialog] = useState(false);

  const jobService = JobService.getInstance();
  const customerService = CustomerService.getInstance();
  const browserTimezone = getBrowserTimezone();
  const locale = i18n.resolvedLanguage || i18n.language || "en-US";
  const environmentConfig = getEtlEnvironmentConfig();

  useEffect(() => {
    if (!jobId || !jobId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      enqueueSnackbar(t("operations.etl.eTLRunDetails.notifications.invalidJobId"), {
        variant: "error",
      });
      void navigate("/admin/data/etl-runs");
      return;
    }

    document.title = t("operations.etl.eTLRunDetails.documentTitle");
    void fetchJobDetails();
  }, [jobId, navigate, t]);

  const fetchJobDetails = async () => {
    if (!jobId || !jobId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      setError(t("operations.etl.eTLRunDetails.errors.invalidJobId"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await jobService.getJobDetails(jobId);

      if (response.success && response.data) {
        setJob(response.data);
        document.title = t("operations.etl.eTLRunDetails.documentTitleWithJobName", {
          jobName: response.data.job.name,
        });

        // Fetch customer name if job has customerId
        if (response.data.job.customerId) {
          const customerResponse = await customerService.getCustomerById(
            response.data.job.customerId,
          );
          if (customerResponse.success && customerResponse.data) {
            setCustomerName(customerResponse.data.name);
          }
        }
      } else {
        throw new Error(
          response.error || t("operations.etl.eTLRunDetails.errors.failedFetchJobDetails"),
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("operations.etl.eTLRunDetails.errors.errorOccurred");
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleRunJob = async () => {
    if (!job?.job.scheduleName) {
      enqueueSnackbar(t("operations.etl.eTLRunDetails.notifications.noScheduleNameAvailable"), {
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await jobService.runJobManually(job.job.scheduleName);
      if (response.success) {
        enqueueSnackbar(t("operations.etl.eTLRunDetails.notifications.jobStartedSuccessfully"), {
          variant: "success",
        });
        await fetchJobDetails();
      } else {
        throw new Error(response.error || t("operations.etl.eTLRunDetails.errors.failedStartJob"));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("operations.etl.eTLRunDetails.errors.failedStartJob");
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
      setShowRunDialog(false);
    }
  };

  const getStatusLabel = (status: string | undefined) => {
    if (!status) {
      return t("operations.etl.eTLRunDetails.text.notAvailable");
    }

    const normalizedStatus = normalizeStatus(status);

    return t(
      `operations.etl.common.status.${etlStatusKeyMap[normalizedStatus] ?? normalizedStatus}`,
    );
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return "default";

    switch (normalizeStatus(status)) {
      case "SUCCESSFUL":
      case "JOB_COMPLETED":
        return "success";
      case "FAILED":
      case "JOB_FAILED":
      case "MACHINE_TIMEOUT":
        return "error";
      case "IN_PROGRESS":
      case "IN PROGRESS":
      case "INPROGRESS":
      case "RUNNING":
      case "JOB_STARTED":
      case "MACHINE_CREATED":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    if (!status) return <ScheduleIcon />;

    switch (normalizeStatus(status)) {
      case "SUCCESSFUL":
      case "JOB_COMPLETED":
        return <CheckIcon />;
      case "FAILED":
      case "JOB_FAILED":
      case "MACHINE_TIMEOUT":
        return <CloseIcon />;
      case "IN_PROGRESS":
      case "IN PROGRESS":
      case "INPROGRESS":
      case "RUNNING":
      case "JOB_STARTED":
      case "MACHINE_CREATED":
        return <ScheduleIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getDurationText = (duration: moment.Duration | null) => {
    if (!duration) {
      return t("operations.etl.eTLRunDetails.text.notAvailable");
    }

    if (duration.asMinutes() < 1) {
      return t("operations.etl.eTLRuns.duration.seconds", {
        seconds: Math.round(duration.asSeconds()),
      });
    }

    if (duration.hours() > 0) {
      return t("operations.etl.eTLRuns.duration.hoursMinutes", {
        hours: duration.hours(),
        minutes: duration.minutes(),
      });
    }

    return t("operations.etl.eTLRuns.duration.minutesSeconds", {
      minutes: duration.minutes(),
      seconds: duration.seconds(),
    });
  };

  if (error) {
    return (
      <PageShell
        breadcrumbs="admin"
        title={t("operations.etl.eTLRunDetails.title")}
        headerActions={
          <Button component={RouterLink} to="/admin/data/etl-runs" startIcon={<ArrowBackIcon />}>
            {t("operations.etl.eTLRunDetails.actions.backToEtlRuns")}
          </Button>
        }
      >
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </PageShell>
    );
  }

  if (!job) {
    return (
      <PageShell breadcrumbs="admin" title={t("operations.etl.eTLRunDetails.title")}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  const jobStatus = job.isSuccessful ? "SUCCESSFUL" : job.isCompleted ? "FAILED" : "IN_PROGRESS";
  const hasReport = hasJsonReport(job.job.report);
  const statusHistory = sortStatusHistory(job.statuses ?? []);
  const cloudLogsUrl = buildCloudLogsUrl(
    job.job.instanceId,
    statusHistory,
    environmentConfig.projectId,
  );
  const totalJobDuration = getTotalJobDuration(statusHistory);
  const outputFiles = [
    {
      type: t("operations.etl.eTLRunDetails.text.report"),
      path: job.job.reportPath,
      url: buildStorageConsoleUrl(job.job.reportPath, environmentConfig.storageBucket),
    },
    {
      type: t("operations.etl.eTLRunDetails.text.data"),
      path: job.job.dataPath,
      url: buildStorageConsoleUrl(job.job.dataPath, environmentConfig.storageBucket),
    },
  ].filter((file): file is { type: string; path: string; url: string | null } =>
    Boolean(file.path),
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("operations.etl.eTLRunDetails.title")}
      headerActions={
        <Stack direction="row" spacing={2}>
          <Button
            component={RouterLink}
            to="/admin/data/etl-runs"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            {t("operations.etl.eTLRunDetails.actions.backToEtlRuns")}
          </Button>
          {cloudLogsUrl && (
            <Button
              href={cloudLogsUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<LogsIcon />}
              variant="outlined"
            >
              {t("operations.etl.eTLRunDetails.actions.openCloudLogs")}
            </Button>
          )}
          {job.job.scheduleName && (
            <Button
              variant="contained"
              startIcon={<RunIcon />}
              onClick={() => setShowRunDialog(true)}
            >
              {t("operations.etl.eTLRunDetails.actions.runJob")}
            </Button>
          )}
        </Stack>
      }
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.jobId")}
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                  {job.job.id}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.jobName")}
                </Typography>
                <Typography variant="body1">{job.job.name}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("common.customer")}
                </Typography>
                <Typography variant="body1">
                  {customerName || t("operations.etl.eTLRunDetails.text.noCustomer")}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.scheduleName")}
                </Typography>
                <Typography variant="body1">
                  {job.job.scheduleName || t("operations.etl.eTLRunDetails.text.notAvailable")}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.status")}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={getStatusIcon(jobStatus)}
                    label={getStatusLabel(jobStatus)}
                    color={getStatusColor(jobStatus)}
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.instanceId")}
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                  {job.job.instanceId || t("operations.etl.eTLRunDetails.text.notAvailable")}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.logs")}
                </Typography>
                {cloudLogsUrl ? (
                  <Button
                    href={cloudLogsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<LogsIcon />}
                    size="small"
                    variant="outlined"
                  >
                    {t("operations.etl.eTLRunDetails.actions.openCloudLogs")}
                  </Button>
                ) : (
                  <Typography variant="body1">
                    {t("operations.etl.eTLRunDetails.text.notAvailable")}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.lastUpdated")}
                </Typography>
                <Typography variant="body1">
                  {job.job.updatedAt ? (
                    <>
                      {formatStoreDateTimeWithTimezone(job.job.updatedAt, browserTimezone, locale)}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({formatRelativeStoreTime(job.job.updatedAt, browserTimezone)})
                      </Typography>
                    </>
                  ) : (
                    t("operations.etl.eTLRunDetails.text.notAvailable")
                  )}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("operations.etl.eTLRunDetails.fields.createdAt")}
                </Typography>
                <Typography variant="body1">
                  {job.job.createdAt ? (
                    <>
                      {formatStoreDateTimeWithTimezone(job.job.createdAt, browserTimezone, locale)}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({formatRelativeStoreTime(job.job.createdAt, browserTimezone)})
                      </Typography>
                    </>
                  ) : (
                    t("operations.etl.eTLRunDetails.text.notAvailable")
                  )}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {outputFiles.length > 0 && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <StorageIcon color="primary" />
                <Typography variant="h6">
                  {t("operations.etl.eTLRunDetails.sections.outputFiles")}
                </Typography>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("operations.etl.eTLRunDetails.fields.type")}</TableCell>
                      <TableCell>{t("operations.etl.eTLRunDetails.fields.path")}</TableCell>
                      <TableCell align="right">
                        {t("operations.etl.eTLRunDetails.fields.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outputFiles.map((file) => (
                      <TableRow key={file.type}>
                        <TableCell>{file.type}</TableCell>
                        <TableCell sx={{ wordBreak: "break-all" }}>
                          {file.url ? (
                            <MuiLink
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              {file.path}
                            </MuiLink>
                          ) : (
                            file.path
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {file.url ? (
                            <Button
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<OpenInNewIcon />}
                              size="small"
                              variant="outlined"
                            >
                              {t("operations.etl.eTLRunDetails.actions.openFile")}
                            </Button>
                          ) : (
                            t("operations.etl.eTLRunDetails.text.notAvailable")
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {hasReport && (
            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: showReport ? 2 : 0 }}
              >
                <Typography variant="h6">
                  {t("operations.etl.eTLRunDetails.sections.reportJson")}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={() => setShowReport((current) => !current)}>
                    {showReport
                      ? t("operations.etl.eTLRunDetails.actions.collapseReport")
                      : t("operations.etl.eTLRunDetails.actions.expandReport")}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={`/admin/data/etl-runs/${job.job.id}/report`}
                    startIcon={<OpenInNewIcon />}
                    variant="outlined"
                  >
                    {t("operations.etl.eTLRunDetails.actions.openReport")}
                  </Button>
                </Stack>
              </Stack>
              {showReport && <JsonReportViewer report={job.job.report} />}
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <HistoryIcon color="primary" />
              <Typography variant="h6">
                {t("operations.etl.eTLRunDetails.sections.statusHistory")}
              </Typography>
            </Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("operations.etl.eTLRunDetails.fields.status")}</TableCell>
                    <TableCell>{t("operations.etl.eTLRunDetails.fields.updatedAt")}</TableCell>
                    <TableCell>{t("operations.etl.eTLRunDetails.fields.elapsed")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusHistory.length > 0 ? (
                    statusHistory.map((status, index) => {
                      const elapsed = getElapsedSincePreviousStatus(statusHistory, index);

                      return (
                        <TableRow key={`${status.status}-${status.createdAt}`}>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(status.status)}
                              label={getStatusLabel(status.status)}
                              color={getStatusColor(status.status)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="column" spacing={0.5}>
                              <Typography variant="body2">
                                {formatStoreDateTimeWithTimezone(
                                  status.createdAt,
                                  browserTimezone,
                                  locale,
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatRelativeStoreTime(status.createdAt, browserTimezone)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{getDurationText(elapsed)}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {t("operations.etl.eTLRunDetails.text.noStatusHistory")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                {t("operations.etl.eTLRunDetails.fields.totalDuration")}:{" "}
                {getDurationText(totalJobDuration)}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <AdminDialog
        open={showRunDialog}
        onClose={() => setShowRunDialog(false)}
        maxWidth="sm"
        title={t("operations.etl.eTLRunDetails.actions.runJob")}
        icon={<RunIcon />}
        cancelAction={{ label: t("common.cancel"), onClick: () => setShowRunDialog(false) }}
        primaryAction={{
          label: t("operations.etl.eTLRunDetails.actions.run"),
          onClick: () => void handleRunJob(),
        }}
      >
        <Typography>{t("operations.etl.eTLRunDetails.text.sureWantRunJob")}</Typography>
      </AdminDialog>
    </PageShell>
  );
};

export default ETLRunDetails;
